import { type ReactElement, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@/ui/primitives';
import { r, s } from '../../theme/tokens';
import { DataTable } from './DataTable';
import { SkeletonDataGrid } from './grid/SkeletonDataGrid';
import { useServerDataGridState } from './grid/useServerDataGridState';
import { applyColumnWidths, wrapDataGridColumns } from './grid/wrapDataGridColumns';
import { LastUpdated } from './LastUpdated';
import type { GridQueryInput, GridQueryResult } from './grid/gridUtils';
import type { SpacingToken } from '../../../src/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type {
  DataGridProps,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowId,
  GridRowParams,
  GridSortModel,
  GridValidRowModel,
} from '@/ui/primitives';
import type { QueryKey } from '@tanstack/react-query';

export type ServerDataTableProps<T extends GridValidRowModel> = {
  queryPath: string;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  initialData?: GridQueryResult<T>;
  columns: Array<GridColDef<T>>;
  getRowId: (row: T) => GridRowId;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  height?: number;
  noRowsText?: string;
  filteredNoRowsText?: string;
  onRowsChange?: (rows: T[]) => void;
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryKeyPrefix?: QueryKey;
  queryKey?: QueryKey;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  onRowClick?: (params: GridRowParams<T>) => void;
  onCellClick?: (params: GridCellParams<T>) => void;
  onColumnHeaderClick?: DataGridProps['onColumnHeaderClick'];
  onQueryInput?: (input: GridQueryInput) => void;
  token?: string | null;
  organisationId?: string | null;
  projectId?: string | null;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  autoHeight?: boolean;
  disableColumnMenu?: boolean;
  disableColumnSorting?: boolean;
  disableColumnResize?: boolean;
  showColumnVerticalBorder?: boolean;
  showCellVerticalBorder?: boolean;
  showToolbar?: boolean;
  showLastUpdated?: boolean;
  getRowHeight?: DataGridProps['getRowHeight'];
  disableCellFocus?: boolean;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  skeletonCellPaddingY?: SpacingToken;
  skeletonGetRowHeight?: DataGridProps['getRowHeight'] | null;
  showSkeletonOnFetch?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  externalLoading?: boolean;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  gridProps?: Partial<DataGridProps>;
};

export const ServerDataTable = <T extends GridValidRowModel>(
  props: ServerDataTableProps<T>,
): ReactElement => {
  const {
    queryPath,
    queryFn,
    initialData,
    columns,
    getRowId,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText = 'No results found',
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    queryKeyPrefix,
    onRefetch,
    onInvalidate,
    onRowClick,
    onCellClick,
    onColumnHeaderClick,
    onQueryInput,
    queryKey: providedQueryKey,
    token: tokenProp,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl = false,
    autoHeight = false,
    disableColumnMenu = true,
    disableColumnSorting = false,
    disableColumnResize = false,
    showColumnVerticalBorder,
    showCellVerticalBorder,
    showToolbar = false,
    showLastUpdated = false,
    getRowHeight,
    disableCellFocus = false,
    hideFooter = false,
    skeletonCellPaddingY,
    skeletonGetRowHeight,
    showSkeletonOnFetch = false,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    externalLoading = false,
    sx,
    containerSx,
    gridProps,
  } = props;
  const [searchParams] = useSearchParams();

  const {
    errorMessage,
    rowsData,
    rowCount,
    showInitialSkeleton,
    queryIsFetching,
    lastUpdatedAt,
    resolvedPageSizeOptions,
    resolvedHeight,
    resolvedNoRowsText,
    resolvedSkeletonRowHeight,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    captureScrollAnchor,
    filterModel,
    handleFilterModelChange,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnWidthModel,
    handleColumnWidthChange,
  } = useServerDataGridState<T>({
    queryPath,
    queryFn,
    initialData,
    columns,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    queryKeyPrefix,
    queryKey: providedQueryKey,
    onRefetch,
    onInvalidate,
    onQueryInput,
    token: tokenProp,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    skeletonGetRowHeight,
    getRowHeight,
  });

  const columnsWithWidths = useMemo(
    () => applyColumnWidths(columns, columnWidthModel),
    [columnWidthModel, columns],
  );
  const wrappedColumns = useMemo(() => wrapDataGridColumns(columnsWithWidths), [columnsWithWidths]);
  const focusStyles: SxProps<Theme> | null = disableCellFocus
    ? {
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
      }
    : null;
  const effectiveDisableCellFocus = disableCellFocus || typeof onColumnHeaderClick === 'function';
  const focusScrollStyles: SxProps<Theme> | null = effectiveDisableCellFocus
    ? {
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
      }
    : null;
  const dataGridSx: SxProps<Theme> = [
    focusScrollStyles ?? focusStyles,
    { width: '100%', minWidth: 0 },
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as SxProps<Theme>;
  const forcedLoadStateParam = searchParams.get('loadState');
  const forcedLoadState =
    forcedLoadStateParam === 'loading' ||
    forcedLoadStateParam === 'empty' ||
    forcedLoadStateParam === 'error'
      ? forcedLoadStateParam
      : null;
  const forcedErrorMessage =
    forcedLoadState === 'error' ? 'Failed to load data (forced by loadState query param).' : null;
  const effectiveErrorMessage = forcedErrorMessage ?? errorMessage;
  const hasForcedNoRows = forcedLoadState === 'empty' || forcedLoadState === 'error';
  const effectiveRowsData = hasForcedNoRows ? [] : rowsData;
  const effectiveRowCount = hasForcedNoRows ? 0 : rowCount;
  const showImmediateLoadingState =
    !hasForcedNoRows && (queryIsFetching || externalLoading) && effectiveRowsData.length === 0;
  const showSkeletonGrid =
    forcedLoadState === 'loading' ||
    showImmediateLoadingState ||
    (showInitialSkeleton && !hasForcedNoRows && effectiveRowsData.length === 0) ||
    (showSkeletonOnFetch && (queryIsFetching || externalLoading) && !hasForcedNoRows);
  const gridReadyState = showSkeletonGrid
    ? 'loading'
    : hasForcedNoRows
      ? forcedLoadState
      : effectiveErrorMessage
        ? 'error'
        : 'ready';
  const shouldOverlaySkeleton = showSkeletonGrid && effectiveRowsData.length > 0;
  const skeletonRowCount = paginationModel.pageSize;
  const skeletonTotalRowCount = showSkeletonGrid ? undefined : effectiveRowCount;
  const containerStyles = containerSx
    ? Array.isArray(containerSx)
      ? containerSx
      : [containerSx]
    : [];
  const { onColumnVisibilityModelChange: gridOnColumnVisibilityModelChange, ...restGridProps } =
    gridProps ?? {};

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        data-testid="grid-ready"
        data-state={gridReadyState}
        data-error={effectiveErrorMessage ?? ''}
        sx={{ display: 'none' }}
      />
      <Box
        data-state={gridReadyState}
        data-fetching={queryIsFetching || externalLoading ? 'true' : 'false'}
        sx={[
          {
            height: resolvedHeight ?? 'auto',
            width: '100%',
            minWidth: 0,
            backgroundColor: 'transparent',
            borderRadius: r('none'),
            boxShadow: 'none',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
            overflowAnchor: 'none',
            '& .MuiDataGrid-root': { border: 'none' },
            ...(focusStyles ?? {}),
          },
          ...containerStyles,
        ]}
      >
        {!showSkeletonGrid || shouldOverlaySkeleton ? (
          <DataTable
            gridProps={{
              rows: effectiveRowsData,
              columns: wrappedColumns,
              loading: queryIsFetching || externalLoading,
              disableRowSelectionOnClick: true,
              rowSelection: false,
              disableColumnMenu,
              disableColumnSorting,
              disableColumnResize,
              showColumnVerticalBorder,
              showCellVerticalBorder,
              hideFooterSelectedRowCount: true,
              pageSizeOptions: resolvedPageSizeOptions,
              paginationMode: 'server',
              sortingMode: 'server',
              filterMode: 'server',
              paginationModel,
              onPaginationModelChange: handlePaginationModelChange,
              rowCount: effectiveRowCount,
              sortModel,
              onSortModelChange: handleSortModelChange,
              onColumnHeaderClick: (...args) => {
                captureScrollAnchor();
                onColumnHeaderClick?.(...args);
              },
              filterModel,
              onFilterModelChange: handleFilterModelChange,
              columnVisibilityModel,
              onColumnVisibilityModelChange: (model, details) => {
                setColumnVisibilityModel(model);
                gridOnColumnVisibilityModelChange?.(model, details);
              },
              onColumnWidthChange: handleColumnWidthChange,
              onRowClick,
              onCellClick,
              getRowId,
              autoHeight,
              getRowHeight,
              showToolbar,
              hideFooter,
              sx: dataGridSx,
              emptyTitle: effectiveErrorMessage ?? resolvedNoRowsText,
              error: Boolean(effectiveErrorMessage),
              errorTitle: effectiveErrorMessage ?? undefined,
              ...restGridProps,
            }}
          />
        ) : null}
        {showSkeletonGrid ? (
          <Box
            sx={
              shouldOverlaySkeleton
                ? {
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                    backgroundColor: 'background.paper',
                  }
                : undefined
            }
          >
            <SkeletonDataGrid<T>
              columns={columnsWithWidths}
              loadingRowCount={skeletonRowCount}
              totalRowCount={skeletonTotalRowCount}
              pageSizeOptions={resolvedPageSizeOptions}
              pageSize={paginationModel.pageSize}
              paginationModel={paginationModel}
              autoHeight={autoHeight}
              showToolbar={showToolbar}
              getRowId={getRowId}
              getRowHeight={resolvedSkeletonRowHeight}
              cellPaddingY={skeletonCellPaddingY}
              columnVisibilityModel={columnVisibilityModel}
              hideFooter={hideFooter}
              sx={dataGridSx}
            />
          </Box>
        ) : null}
      </Box>
      {showLastUpdated ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: s('sm'), pt: s('sm') }}>
          <LastUpdated timestamp={lastUpdatedAt} />
        </Box>
      ) : null}
    </Box>
  );
};
